import { Component, effect, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { CopyComponent, NotificationsService } from '@ui';
import * as QRCode from 'qrcode';

import { TotpAuthStore } from './totp-auth.store';
import { TotpCodeComponent } from './totp-code.component';

type TotpEnrollStep = 'intro' | 'scan' | 'code' | 'recovery-codes';

@Component({
  imports: [
    MatDialogModule,
    FormsModule,
    MatProgressSpinner,
    MatCheckbox,
    CopyComponent,
    TotpCodeComponent,
  ],
  selector: 'avs-totp-enroll-dialog',
  templateUrl: './totp-enroll-dialog.component.html',
})
export class TotpEnrollDialogComponent implements OnInit {
  protected hasSavedRecoveryCodes = signal(false);
  protected isCodeComplete = signal(false);
  protected qrCodeDataUrl = signal<string | undefined>(undefined);
  protected step = signal<TotpEnrollStep>('intro');
  protected store = inject(TotpAuthStore);
  protected token = signal('');

  private notificationService = inject(NotificationsService);
  private handledError: string | null = null;

  constructor() {
    effect(async () => {
      const otpauthUrl = this.store.otpauthUrl();

      if (otpauthUrl) {
        this.qrCodeDataUrl.set(await QRCode.toDataURL(otpauthUrl));
      }
    });

    effect(() => {
      if (this.store.recoveryCodes()?.length) {
        this.step.set('recovery-codes');
      }
    });

    effect(() => {
      const error = this.store.error();

      if (error && error !== this.handledError && this.step() === 'code') {
        this.handledError = error;
        this.notificationService.showErrorMessage(error);
      }
    });
  }

  ngOnInit() {
    this.store.reset();
    this.store.enroll();
  }

  protected back() {
    if (this.step() === 'code') this.step.set('scan');
    else if (this.step() === 'scan') this.step.set('intro');
  }

  protected done() {
    this.store.completeEnrollment();
  }

  protected next() {
    if (this.step() === 'intro') this.step.set('scan');
    else if (this.step() === 'scan') this.step.set('code');
  }

  protected onTokenChange(value: string) {
    this.token.set(value);
    this.isCodeComplete.set(value.length === 6);

    if (value && this.store.error()) {
      this.handledError = null;
      this.store.clearError();
    }
  }

  protected verify() {
    if (this.isCodeComplete()) {
      this.store.confirm(this.token());
    }
  }
}
